import { supabase } from './supabase';

// ─── Language ─────────────────────────────────────────────────────────────────
// Drives which WhatsApp template language the customer receives in automations.
// Must stay within the customers.language CHECK constraint (ar | tr | en).

export type CustomerLanguage = 'ar' | 'tr' | 'en';

export const LANGUAGE_OPTIONS: Array<{ value: CustomerLanguage; label: string }> = [
  { value: 'ar', label: 'Arabic' },
  { value: 'tr', label: 'Turkish' },
  { value: 'en', label: 'English' },
];

/** Coerce any stored/unknown value to a valid language, defaulting to 'ar'. */
export function normalizeLanguage(value: unknown): CustomerLanguage {
  return value === 'tr' || value === 'en' ? value : 'ar';
}

export function languageLabel(value: string | null | undefined): string {
  return LANGUAGE_OPTIONS.find((option) => option.value === value)?.label ?? 'Arabic';
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Customer {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  nationality: string | null;
  language: string;
  id_type: string | null;
  id_number: string | null;
  birth_date: string | null;
  address: string | null;
  driving_license_number: string | null;
  license_issue_date: string | null;
  notes: string | null;
  created_at: string | null;
  id_photo_url: string | null;
  id_photo_back_url: string | null;
  driving_license_photo_url: string | null;
  driving_license_back_url: string | null;
  entry_stamp_photo_url: string | null;
  is_banned: boolean;
  ban_reason: string | null;
  banned_at: string | null;
  banned_by: string | null;
}

type Joined<T> = T | T[] | null;

const one = <T,>(value: Joined<T> | undefined): T | null =>
  (Array.isArray(value) ? value[0] ?? null : value ?? null);

export interface DirectoryBooking {
  id: number;
  customer_id: string;
  car_id: number;
  start_date: string;
  end_date: string;
  status: string | null;
  total_amount: number | null;
  is_currently_active: boolean | null;
  cars: Joined<{ plate_number: string }>;
}

export interface LedgerRow {
  booking_id: number;
  customer_id: string;
  type: string;
  amount: number;
  direction: 'IN' | 'OUT';
}

export interface DatedLedgerRow extends LedgerRow {
  created_at: string | null;
}

export interface LedgerEntry extends DatedLedgerRow {
  id: string;
  car_id: number;
  description: string | null;
}

interface DirectoryReview {
  customer_id: string;
  our_rating: number | null;
}

export interface CustomerDirectory {
  customers: Customer[];
  bookings: DirectoryBooking[];
  ledger: DatedLedgerRow[];
  reviews: DirectoryReview[];
}

export interface ProfileCar {
  plate_number: string;
  car_photo_urls: Array<{ url: string }> | null;
  model_group: Joined<{ name: string; image_url: string | null; min_age: number | null }>;
}

export interface ProfileBooking {
  id: number;
  booking_number: string | null;
  car_id: number;
  start_date: string;
  end_date: string;
  status: string | null;
  insurance_type: string | null;
  total_amount: number | null;
  is_currently_active: boolean | null;
  cars: Joined<ProfileCar>;
}

export interface CustomerFine {
  id: string;
  status: string | null;
  plate_number: string;
  violation_number: string | null;
  amount: number | null;
  location: string | null;
  violation_date: string | null;
  description: string | null;
  fine_image_url: string | null;
  fine_pdf_url: string | null;
}

export interface CustomerHgs {
  id: number;
  plate_number: string;
  transit_datetime: string;
  toll_location: string | null;
  entry_location: string | null;
  exit_location: string | null;
  amount: number;
  record_type: string | null;
}

export type ReviewStatus = 'pending_review' | 'awaiting_send' | 'sent' | 'rejected';

export interface CustomerReview {
  id: number;
  our_rating: number | null;
  status: ReviewStatus;
  rated_at: string | null;
  rated_by: string | null;
  review_sent_at: string | null;
}

export interface CustomerProfile {
  customer: Customer;
  bookings: ProfileBooking[];
  ledger: LedgerEntry[];
  fines: CustomerFine[];
  hgs: CustomerHgs[];
  review: CustomerReview | null;
  raterName: string | null;
  bannedByName: string | null;
}

// ─── Documents ────────────────────────────────────────────────────────────────

export type DocumentKey =
  | 'id_photo_url'
  | 'id_photo_back_url'
  | 'driving_license_photo_url'
  | 'driving_license_back_url'
  | 'entry_stamp_photo_url';

/** `prefix` is the object name BookingsPage already uses in `customers_doc`. */
export const DOCUMENT_SLOTS: Array<{ key: DocumentKey; label: string; prefix: string }> = [
  { key: 'id_photo_url', label: 'ID — front', prefix: 'id-photo' },
  { key: 'id_photo_back_url', label: 'ID — back', prefix: 'id-photo-back' },
  { key: 'driving_license_photo_url', label: 'License — front', prefix: 'driving-license' },
  { key: 'driving_license_back_url', label: 'License — back', prefix: 'driving-license-back' },
  { key: 'entry_stamp_photo_url', label: 'Entry stamp', prefix: 'entry-stamp' },
];

export function documentCount(customer: Pick<Customer, DocumentKey>): number {
  return DOCUMENT_SLOTS.filter((slot) => Boolean(customer[slot.key])).length;
}

// ─── Dates ────────────────────────────────────────────────────────────────────

const istanbulDay = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Istanbul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** `YYYY-MM-DD` in Istanbul, where every branch is. */
export function istanbulDate(value: Date | string = new Date()): string {
  return istanbulDay.format(typeof value === 'string' ? new Date(value) : value);
}

const dayNumber = (date: string) => Math.round(Date.parse(`${date.slice(0, 10)}T00:00:00Z`) / 86400000);

export function daysBetween(from: string, to: string): number {
  return dayNumber(to) - dayNumber(from);
}

export function formatDate(date: string | null | undefined): string {
  if (!date) return '—';
  const [year, month, day] = date.slice(0, 10).split('-');
  return day ? `${day}.${month}.${year}` : date;
}

/** Whole years from `from` to `today`, or null when there's no date. */
export function yearsSince(from: string | null | undefined, today: string): number | null {
  if (!from) return null;
  const [fromYear, fromMonth, fromDay] = from.slice(0, 10).split('-').map(Number);
  const [year, month, day] = today.split('-').map(Number);
  const hadAnniversary = month > fromMonth || (month === fromMonth && day >= fromDay);
  return year - fromYear - (hadAnniversary ? 0 : 1);
}

// ─── Money ────────────────────────────────────────────────────────────────────

const wholeLira = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 });

/** Lira without kuruş, for tiles, pills and table cells. */
export function formatLira(amount: number): string {
  return wholeLira.format(amount);
}

/** What the customer owes: OUT − IN. Negative means we're holding their money. */
export function ledgerBalance(entries: Array<Pick<LedgerRow, 'amount' | 'direction'>>): number {
  const balance = entries.reduce(
    (sum, entry) => sum + (entry.direction === 'OUT' ? Number(entry.amount) : -Number(entry.amount)),
    0,
  );
  return Math.round(balance * 100) / 100;
}

export type BalanceState = 'owes' | 'credit' | 'clear';

export function balanceState(balance: number): BalanceState {
  if (balance > 0) return 'owes';
  return balance < 0 ? 'credit' : 'clear';
}

/**
 * An OUT row is a charge unless it hands money back. `type` is free text, which
 * is why refunds appear as refund, REFUND, REF and REFOUND.
 */
export function isCharge(entry: Pick<LedgerRow, 'type' | 'direction'>): boolean {
  if (entry.direction !== 'OUT') return false;
  const type = entry.type.trim().toLowerCase();
  return !type.startsWith('ref') && type !== 'deposit';
}

/**
 * What the customer has spent across their bookings. `bookings.total_amount` is
 * empty on most bookings and half of them have no ledger rows, so neither source
 * is complete: each booking counts as whichever is larger.
 */
export function totalSpent(
  bookings: Array<{ id: number; status: string | null; total_amount: number | null }>,
  ledger: Array<Pick<LedgerRow, 'booking_id' | 'type' | 'direction' | 'amount'>>,
): number {
  const charged = new Map<number, number>();
  ledger.filter(isCharge).forEach((entry) => {
    charged.set(entry.booking_id, (charged.get(entry.booking_id) ?? 0) + Number(entry.amount));
  });
  return bookings
    .filter((booking) => booking.status !== 'cancelled')
    .reduce((sum, booking) => sum + Math.max(charged.get(booking.id) ?? 0, Number(booking.total_amount ?? 0)), 0);
}

// ─── Directory ────────────────────────────────────────────────────────────────

export const VIP_MIN_BOOKINGS = 3;
export const VIP_TOP_SHARE = 0.1;
export const LOW_RATING_MAX = 2;

export interface CustomerSummary {
  customer: Customer;
  name: string;
  bookingCount: number;
  spent: number;
  balance: number;
  documents: number;
  rating: number | null;
  lastBooking: string | null;
  activeNow: boolean;
  currentPlate: string | null;
  isVip: boolean;
  isNew: boolean;
}

export function customerName(customer: Pick<Customer, 'first_name' | 'last_name'>): string {
  return `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim() || 'Unnamed customer';
}

/** The spend a customer needs to be in the top 10% of those who've spent anything. */
export function vipSpendThreshold(spends: number[]): number {
  const ranked = spends.filter((spend) => spend > 0).sort((a, b) => b - a);
  if (ranked.length === 0) return Infinity;
  return ranked[Math.max(Math.ceil(ranked.length * VIP_TOP_SHARE), 1) - 1];
}

function groupBy<T>(rows: T[], key: (row: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  rows.forEach((row) => {
    const value = key(row);
    const group = groups.get(value);
    if (group) group.push(row);
    else groups.set(value, [row]);
  });
  return groups;
}

export function summarizeCustomers(directory: CustomerDirectory, today: string): CustomerSummary[] {
  const bookingsByCustomer = groupBy(directory.bookings, (booking) => booking.customer_id);
  const ledgerByCustomer = groupBy(directory.ledger, (entry) => entry.customer_id);
  const ratings = new Map(directory.reviews.map((review) => [review.customer_id, review.our_rating]));
  const month = today.slice(0, 7);

  const summaries = directory.customers.map((customer): CustomerSummary => {
    const bookings = bookingsByCustomer.get(customer.id) ?? [];
    const ledger = ledgerByCustomer.get(customer.id) ?? [];
    const live = bookings.filter((booking) => booking.is_currently_active);
    const kept = bookings.filter((booking) => booking.status !== 'cancelled');
    return {
      customer,
      name: customerName(customer),
      bookingCount: kept.length,
      spent: totalSpent(bookings, ledger),
      balance: ledgerBalance(ledger),
      documents: documentCount(customer),
      rating: ratings.get(customer.id) ?? null,
      lastBooking: kept.reduce<string | null>((latest, booking) => (
        !latest || booking.start_date > latest ? booking.start_date : latest
      ), null),
      activeNow: live.length > 0,
      currentPlate: one(live[0]?.cars)?.plate_number ?? null,
      isVip: false,
      isNew: Boolean(customer.created_at) && istanbulDate(customer.created_at as string).slice(0, 7) === month,
    };
  });

  const threshold = vipSpendThreshold(summaries.map((summary) => summary.spent));
  return summaries.map((summary) => ({
    ...summary,
    isVip: summary.bookingCount >= VIP_MIN_BOOKINGS || summary.spent >= threshold,
  }));
}

export type CustomerSegment = 'all' | 'active' | 'owes' | 'credit' | 'documents' | 'vip' | 'low' | 'new' | 'banned';

export function inSegment(summary: CustomerSummary, segment: CustomerSegment): boolean {
  switch (segment) {
    case 'active': return summary.activeNow;
    case 'owes': return summary.balance > 0;
    case 'credit': return summary.balance < 0;
    case 'documents': return summary.documents < DOCUMENT_SLOTS.length;
    case 'vip': return summary.isVip;
    case 'low': return summary.rating !== null && summary.rating <= LOW_RATING_MAX;
    case 'new': return summary.isNew;
    case 'banned': return summary.customer.is_banned;
    default: return true;
  }
}

export function customerStats(summaries: CustomerSummary[]) {
  const owing = summaries.filter((summary) => summary.balance > 0);
  return {
    total: summaries.length,
    active: summaries.filter((summary) => summary.activeNow).length,
    owingCount: owing.length,
    owingTotal: owing.reduce((sum, summary) => sum + summary.balance, 0),
    newThisMonth: summaries.filter((summary) => summary.isNew).length,
  };
}

const compact = (value: string) => value.replace(/[\s\-+()]/g, '').toLowerCase();

/** Name, phone (digits only), ID, nationality, or the plate they're driving now. */
export function matchesSearch(summary: CustomerSummary, query: string): boolean {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  const { customer, name, currentPlate } = summary;
  const bare = compact(term);
  return (
    name.toLowerCase().includes(term) ||
    (bare !== '' && compact(customer.phone ?? '').includes(bare)) ||
    (customer.id_number ?? '').toLowerCase().includes(term) ||
    (customer.nationality ?? '').toLowerCase().includes(term) ||
    (bare !== '' && compact(currentPlate ?? '').includes(bare))
  );
}

// ─── Reading ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 1000;
const CUSTOMER_COLUMNS = [
  'id', 'first_name', 'last_name', 'phone', 'email', 'nationality', 'language', 'id_type', 'id_number',
  'birth_date', 'address', 'driving_license_number', 'license_issue_date', 'notes', 'created_at',
  'id_photo_url', 'id_photo_back_url', 'driving_license_photo_url', 'driving_license_back_url',
  'entry_stamp_photo_url', 'is_banned', 'ban_reason', 'banned_at', 'banned_by',
].join(', ');

type PageResult = PromiseLike<{ data: unknown; error: { message: string } | null }>;

/** PostgREST stops at 1000 rows without saying so, and the ledger is already past that. */
async function fetchAll<T>(page: (from: number, to: number) => PageResult): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await page(from, from + PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    const batch = (data ?? []) as T[];
    rows.push(...batch);
    if (batch.length < PAGE_SIZE) return rows;
  }
}

export async function fetchCustomerDirectory(): Promise<CustomerDirectory> {
  const [customers, bookings, ledger, reviews] = await Promise.all([
    fetchAll<Customer>((from, to) => supabase
      .from('customers').select(CUSTOMER_COLUMNS).order('first_name').order('id').range(from, to)),
    fetchAll<DirectoryBooking>((from, to) => supabase
      .from('bookings')
      .select('id, customer_id, car_id, start_date, end_date, status, total_amount, is_currently_active, cars(plate_number)')
      .order('id')
      .range(from, to)),
    fetchAll<DatedLedgerRow>((from, to) => supabase
      .from('customer_accounting_ledger')
      .select('booking_id, customer_id, type, amount, direction, created_at')
      .order('id')
      .range(from, to)),
    fetchAll<DirectoryReview>((from, to) => supabase
      .from('customer_reviews')
      .select('customer_id, our_rating')
      .is('deleted_at', null)
      .order('id')
      .range(from, to)),
  ]);
  return { customers, bookings, ledger, reviews };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Null when no such customer exists. */
export async function fetchCustomerProfile(id: string): Promise<CustomerProfile | null> {
  if (!UUID.test(id)) return null;

  const [customer, bookings, ledger, fines, hgs, review] = await Promise.all([
    supabase.from('customers').select(CUSTOMER_COLUMNS).eq('id', id).maybeSingle(),
    supabase
      .from('bookings')
      .select(`id, booking_number, car_id, start_date, end_date, status, insurance_type, total_amount,
        is_currently_active, cars(plate_number, car_photo_urls, model_group(name, image_url, min_age))`)
      .eq('customer_id', id)
      .order('start_date', { ascending: false }),
    supabase
      .from('customer_accounting_ledger')
      .select('id, booking_id, customer_id, car_id, type, description, amount, direction, created_at')
      .eq('customer_id', id)
      .order('created_at', { ascending: true }),
    supabase
      .from('traffic_fines')
      .select('id, status, plate_number, violation_number, amount, location, violation_date, description, fine_image_url, fine_pdf_url')
      .eq('customer_id', id)
      .order('violation_date', { ascending: false }),
    fetchAll<CustomerHgs>((from, to) => supabase
      .from('hgs_transactions')
      .select('id, plate_number, transit_datetime, toll_location, entry_location, exit_location, amount, record_type')
      .eq('customer_id', id)
      .order('transit_datetime', { ascending: false })
      .order('id')
      .range(from, to)),
    supabase
      .from('customer_reviews')
      .select('id, our_rating, status, rated_at, rated_by, review_sent_at')
      .eq('customer_id', id)
      .is('deleted_at', null)
      .maybeSingle(),
  ]);

  const failure = customer.error ?? bookings.error ?? ledger.error ?? fines.error ?? review.error;
  if (failure) throw new Error(failure.message);
  if (!customer.data) return null;

  const reviewRow = review.data as CustomerReview | null;
  const customerRow = customer.data as unknown as Customer;
  const [raterName, bannedByName] = await Promise.all([
    fetchProfileName(reviewRow?.rated_by),
    fetchProfileName(customerRow.banned_by),
  ]);
  return {
    customer: customerRow,
    bookings: (bookings.data ?? []) as unknown as ProfileBooking[],
    ledger: (ledger.data ?? []) as LedgerEntry[],
    fines: (fines.data ?? []) as CustomerFine[],
    hgs,
    review: reviewRow,
    raterName,
    bannedByName,
  };
}

async function fetchProfileName(profileId: string | null | undefined): Promise<string | null> {
  if (!profileId) return null;
  const { data } = await supabase.from('profiles').select('full_name').eq('id', profileId).maybeSingle();
  return (data as { full_name: string | null } | null)?.full_name ?? null;
}

// ─── Writing ──────────────────────────────────────────────────────────────────

export type CustomerPatch = Partial<Omit<Customer, 'id' | 'created_at' | 'is_banned' | 'ban_reason' | 'banned_at' | 'banned_by'>>;

/** Same rules as the database's norm_id_number and valid_id_number. */
export function normalizeIdNumber(value: string | null | undefined): string {
  return (value ?? '').replace(/[\s.-]/g, '').toUpperCase();
}

export function isValidIdNumber(value: string | null | undefined): boolean {
  const id = normalizeIdNumber(value);
  return id.length >= 6 && !/^(.)\1*$/.test(id);
}

/**
 * `id_number` for an update, only if it changed. The duplicate-ID trigger runs
 * whenever the column is written, so resending an unchanged value fails for any
 * customer who still shares an ID with another record.
 */
export function idNumberChange(before: string | null | undefined, after: string | null | undefined) {
  const next = (after ?? '').trim() || null;
  return next === ((before ?? '').trim() || null) ? {} : { id_number: next };
}

export type CustomerWriteError =
  | { kind: 'banned'; text: string }
  | { kind: 'duplicate_id'; text: string; customerId: string; name: string }
  | { kind: 'other'; text: string };

/** Turns what the database rules raise into something a person can read. */
export function friendlyCustomerError(message: string): CustomerWriteError {
  const banned = message.match(/CUSTOMER_BANNED:.*Reason:\s*([\s\S]*)$/);
  if (banned) {
    const reason = banned[1].trim();
    return { kind: 'banned', text: reason && reason !== '-' ? `This customer is banned — ${reason}` : 'This customer is banned.' };
  }
  const duplicate = message.match(/DUPLICATE_ID_NUMBER:.*registered for ([\s\S]*) \(([0-9a-f-]{36})\)/i);
  if (duplicate) {
    const name = duplicate[1].trim() || 'another customer';
    return { kind: 'duplicate_id', text: `This ID belongs to ${name}`, customerId: duplicate[2], name };
  }
  if (message.trim().toLowerCase() === 'forbidden') return { kind: 'other', text: 'Only admins can do this.' };
  return { kind: 'other', text: message };
}

export async function updateCustomer(id: string, patch: CustomerPatch): Promise<Customer> {
  const { data, error } = await supabase
    .from('customers')
    .update(patch)
    .eq('id', id)
    .select(CUSTOMER_COLUMNS)
    .single();
  if (error) throw new Error(friendlyCustomerError(error.message).text);
  return data as unknown as Customer;
}

// ─── Finding customers, and bans ──────────────────────────────────────────────

export interface CustomerSearchResult {
  id: string;
  full_name: string;
  phone: string | null;
  id_number: string | null;
  nationality: string | null;
  is_banned: boolean;
  ban_reason: string | null;
  bookings_count: number;
  balance: number;
  last_booking: string | null;
  match_type: 'id_number' | 'phone' | 'name';
}

export async function searchCustomers(query: string, limit = 8): Promise<CustomerSearchResult[]> {
  const { data, error } = await supabase.rpc('search_customers', { p_query: query, p_limit: limit });
  if (error) throw new Error(error.message);
  return ((data ?? []) as CustomerSearchResult[]).map((row) => ({
    ...row,
    bookings_count: Number(row.bookings_count),
    balance: Number(row.balance),
  }));
}

export async function customersWithPhone(
  phone: string,
  exclude: string | null,
): Promise<Array<{ id: string; full_name: string; is_banned: boolean }>> {
  const { data, error } = await supabase.rpc('customers_with_phone', { p_phone: phone, p_exclude: exclude });
  if (error) throw new Error(error.message);
  return (data ?? []) as Array<{ id: string; full_name: string; is_banned: boolean }>;
}

export interface BanMatch {
  customer_id: string;
  full_name: string;
  ban_reason: string | null;
  matched_on: 'customer' | 'id_number' | 'phone';
}

/** The ban that applies to this person: their own, or one on the same ID or phone. */
export async function customerBanMatch(
  customerId: string | null,
  idNumber: string | null,
  phone: string | null,
): Promise<BanMatch | null> {
  const { data, error } = await supabase.rpc('customer_ban_match', {
    p_customer: customerId,
    p_id_number: idNumber,
    p_phone: phone,
  });
  if (error) throw new Error(error.message);
  return ((data ?? []) as BanMatch[])[0] ?? null;
}

/** For flows that start from a booking, like an extension or a replacement. */
export async function checkCustomerBan(customerId: string): Promise<BanMatch | null> {
  const { data, error } = await supabase.from('customers').select('id_number, phone').eq('id', customerId).maybeSingle();
  if (error) throw new Error(error.message);
  const customer = data as { id_number: string | null; phone: string | null } | null;
  return customerBanMatch(customerId, customer?.id_number ?? null, customer?.phone ?? null);
}

export async function setCustomerBan(customerId: string, banned: boolean, reason: string | null): Promise<void> {
  const { error } = await supabase.rpc('admin_set_customer_ban', {
    p_customer: customerId,
    p_banned: banned,
    p_reason: reason,
  });
  if (error) throw new Error(friendlyCustomerError(error.message).text);
}

/** OUT − IN across every booking, the same figure `extend_booking` works from. */
export async function fetchCustomerBalance(customerId: string): Promise<number> {
  const rows = await fetchAll<Pick<LedgerRow, 'amount' | 'direction'>>((from, to) => supabase
    .from('customer_accounting_ledger')
    .select('amount, direction')
    .eq('customer_id', customerId)
    .order('id')
    .range(from, to));
  return ledgerBalance(rows);
}

/**
 * Hands a booking's money records to its new customer. Not one transaction: the
 * booking itself has already moved, so a failure here is reported rather than undone.
 */
export async function moveBookingToCustomer(bookingId: number, customerId: string): Promise<void> {
  const results = await Promise.all([
    supabase.from('customer_accounting_ledger').update({ customer_id: customerId }).eq('booking_id', bookingId),
    supabase.from('hgs_transactions').update({ customer_id: customerId }).eq('booking_id', bookingId),
    supabase.from('replacement_sheets').update({ customer_id: customerId }).eq('original_booking_id', bookingId),
  ]);
  if (results.some((result) => result.error)) {
    throw new Error("The booking moved, but some of its records didn't. Check its ledger.");
  }
}

/** `extend_booking` writes its own descriptions, so a USD note goes on afterwards. */
export async function appendLedgerNote(bookingId: number, type: 'rental' | 'payment', amount: number, note: string): Promise<void> {
  const { data, error } = await supabase
    .from('customer_accounting_ledger')
    .select('id, description')
    .eq('booking_id', bookingId)
    .eq('type', type)
    .eq('amount', amount)
    .like('description', type === 'rental' ? 'Rental extension%' : 'Extension payment%')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) throw new Error(error?.message ?? 'Ledger row not found');
  const row = data as { id: string; description: string };
  const { error: updateError } = await supabase
    .from('customer_accounting_ledger')
    .update({ description: `${row.description} ${note}` })
    .eq('id', row.id);
  if (updateError) throw new Error(updateError.message);
}

/** What someone typed into the search, sorted into the field it belongs in. */
export function prefillFromQuery(query: string): { first_name?: string; last_name?: string; id_number?: string; phone?: string } {
  const text = query.trim().replace(/\s+/g, ' ');
  if (!text) return {};
  if (/^[+\d][\d\s()-]*$/.test(text)) {
    return /^(\+|0)/.test(text) ? { phone: text.replace(/[^\d+]/g, '') } : { id_number: text.replace(/\s/g, '') };
  }
  if (/\d/.test(text) && !text.includes(' ')) return { id_number: text };
  const [first, ...rest] = text.split(' ');
  return { first_name: first, last_name: rest.join(' ') };
}

/** Keyed on the customer id, never the name: Storage rejects non-ASCII object keys. */
export async function uploadCustomerDocument(customerId: string, slot: DocumentKey, file: File): Promise<Customer> {
  const prefix = DOCUMENT_SLOTS.find((item) => item.key === slot)?.prefix ?? 'document';
  const extension = (file.name.split('.').pop() ?? '').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = `${customerId}/${prefix}-${Date.now()}.${extension}`;

  const { error } = await supabase.storage.from('customers_doc').upload(path, file, { upsert: true });
  if (error) throw new Error(error.message);

  const { data } = supabase.storage.from('customers_doc').getPublicUrl(path);
  return updateCustomer(customerId, { [slot]: `${data.publicUrl}?v=${Date.now()}` });
}

export type PaymentType = 'payment' | 'deposit';

/**
 * Same rule as the Google Reviews page: 5★ is worth asking for a review, anything
 * less isn't. A request that already went out stays sent, so re-rating never
 * queues a second one.
 */
export function reviewStatusFor(rating: number, current: ReviewStatus | null | undefined): ReviewStatus {
  if (current === 'sent') return 'sent';
  return rating === 5 ? 'awaiting_send' : 'rejected';
}

export async function rateCustomer(input: {
  customerId: string;
  rating: number;
  bookingId: number;
  currentStatus: ReviewStatus | null | undefined;
}): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase.from('customer_reviews').upsert({
    customer_id: input.customerId,
    reference_booking_id: input.bookingId,
    our_rating: input.rating,
    status: reviewStatusFor(input.rating, input.currentStatus),
    rated_by: user?.id ?? null,
    rated_at: new Date().toISOString(),
    deleted_at: null,
  }, { onConflict: 'customer_id' });
  if (error) throw new Error(error.message);
}

// ─── Profile ──────────────────────────────────────────────────────────────────

export function bookingCar(booking: ProfileBooking) {
  const car = one(booking.cars);
  const model = one(car?.model_group);
  return {
    plate: car?.plate_number ?? null,
    model: model?.name ?? null,
    minAge: model?.min_age ?? null,
    photo: car?.car_photo_urls?.[0]?.url ?? model?.image_url ?? null,
  };
}

/** The rental happening now, otherwise the next one that's booked. */
export function focusBooking(bookings: ProfileBooking[], today: string): ProfileBooking | null {
  const live = bookings.find((booking) => booking.is_currently_active);
  if (live) return live;
  const upcoming = bookings
    .filter((booking) => booking.start_date >= today && booking.status !== 'cancelled' && booking.status !== 'completed')
    .sort((a, b) => a.start_date.localeCompare(b.start_date));
  return upcoming[0] ?? null;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

export function bookingCountdown(booking: ProfileBooking, today: string): string {
  if (!booking.is_currently_active) {
    const toStart = daysBetween(today, booking.start_date);
    return toStart === 0 ? 'Starts today' : `Starts in ${plural(toStart, 'day')}`;
  }
  const toEnd = daysBetween(today, booking.end_date);
  if (toEnd === 0) return 'Due back today';
  return toEnd > 0 ? `Due back in ${plural(toEnd, 'day')}` : `${plural(-toEnd, 'day')} overdue`;
}

export function rentalDays(bookings: ProfileBooking[]): number {
  return bookings
    .filter((booking) => booking.status !== 'cancelled')
    .reduce((sum, booking) => sum + Math.max(daysBetween(booking.start_date, booking.end_date), 1), 0);
}

export interface LedgerLine extends LedgerEntry {
  running: number;
}

/** Newest first, each row carrying what the customer owed once it was entered. */
export function runningLedger(entries: LedgerEntry[]): LedgerLine[] {
  let running = 0;
  return entries
    .map((entry) => {
      running = Math.round((running + (entry.direction === 'OUT' ? 1 : -1) * Number(entry.amount)) * 100) / 100;
      return { ...entry, running };
    })
    .reverse();
}

const TYPE_LABELS: Record<string, string> = {
  hgs: 'HGS',
  car_wash: 'Car wash',
  extra_km: 'Extra km',
};

export function ledgerTypeLabel(type: string): string {
  const key = type.trim().toLowerCase();
  if (TYPE_LABELS[key]) return TYPE_LABELS[key];
  if (type !== type.toLowerCase()) return type.trim();
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export type AgeCheck =
  | { state: 'missing' }
  | { state: 'no_rule'; age: number }
  | { state: 'ok' | 'under'; age: number; minAge: number; model: string };

/** Against the car they're renting now or next, otherwise the one they last rented. */
export function checkAge(customer: Pick<Customer, 'birth_date'>, bookings: ProfileBooking[], today: string): AgeCheck {
  const age = yearsSince(customer.birth_date, today);
  if (age === null) return { state: 'missing' };
  const target = focusBooking(bookings, today) ?? bookings[0];
  const car = target ? bookingCar(target) : null;
  if (!car?.minAge) return { state: 'no_rule', age };
  return { state: age >= car.minAge ? 'ok' : 'under', age, minAge: car.minAge, model: car.model ?? 'this car' };
}

export type ActivityKind = 'booking' | 'payment' | 'fine' | 'review';

export interface ActivityItem {
  id: string;
  kind: ActivityKind;
  date: string;
  title: string;
  detail: string;
}

export function buildActivity(profile: CustomerProfile, limit = 10): ActivityItem[] {
  const items: ActivityItem[] = [
    ...profile.bookings.map((booking): ActivityItem => {
      const car = bookingCar(booking);
      return {
        id: `booking-${booking.id}`,
        kind: 'booking',
        date: booking.start_date,
        title: `Booking ${booking.booking_number ?? `#${booking.id}`}`,
        detail: [car.plate, car.model].filter(Boolean).join(' · '),
      };
    }),
    ...profile.ledger
      .filter((entry) => entry.direction === 'IN' && entry.created_at)
      .map((entry): ActivityItem => ({
        id: `ledger-${entry.id}`,
        kind: 'payment',
        date: entry.created_at as string,
        title: `${ledgerTypeLabel(entry.type)} received`,
        detail: formatLira(Number(entry.amount)),
      })),
    ...profile.fines
      .filter((fine) => fine.violation_date)
      .map((fine): ActivityItem => ({
        id: `fine-${fine.id}`,
        kind: 'fine',
        date: fine.violation_date as string,
        title: 'Traffic fine',
        detail: [fine.plate_number, fine.amount === null ? null : formatLira(Number(fine.amount))].filter(Boolean).join(' · '),
      })),
  ];

  if (profile.review?.rated_at && profile.review.our_rating) {
    items.push({
      id: `review-${profile.review.id}`,
      kind: 'review',
      date: profile.review.rated_at,
      title: 'Rated by us',
      detail: `${profile.review.our_rating} of 5`,
    });
  }

  return items.sort((a, b) => b.date.localeCompare(a.date)).slice(0, limit);
}
